import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useColumnSortFunc } from "../blocks/ColumnSort/useColumnSortFunc";
import { useOrdersCriteriaPredicate } from "../blocks/MyQuotes/filters/Criteria/useOrdersCriteriaPredicate";
import { useOrdersDatePredicate } from "../blocks/MyQuotes/filters/Date/useOrdersDatePredicate";
import { useOrdersSizeLimit } from "../blocks/MyQuotes/filters/Size/useOrdersSizeLimit";
import { useLanguage } from "../i18n/useLanguage";
import { useDealerAccountInfo } from "../stores/hooks/useDealerAccountInfo";
import { OrderDetails } from "../stores/slices/cartSlice";
import { removeHostFromUrl } from "../util/removeHostFromUrl";

const dateFields = new Set(["dateCreated"]);
const numericFields = new Set(["orderNumber", "orderTotal"]);

export const useGetDealerOrderHistory = (): ModifiedSWRResponse<
  FetcherResult<Payload>,
  MappedDealerOrderItem[]
> => {
  const { dealerId } = useDealerAccountInfo();
  const { t } = useTranslation();
  const [language] = useLanguage();

  const datePredicate = useOrdersDatePredicate();
  const criteriaPredicate = useOrdersCriteriaPredicate();
  const size = useOrdersSizeLimit();

  const sortFunc = useColumnSortFunc("orderReport", {
    dateFields,
    numericFields,
  });

  const { data, ...rest } = useSwrGet<Payload>(
    "/retailSiteWebService/shopping/dealerOrder/getDealerOrderHistory.php",
    {
      dealerId,
    }
  );

  const mappedData = useMemo(
    () =>
      data
        ?.filter(criteriaPredicate)
        // .filter(datePredicate)
        .map(
          ({
            confirmationNumber,
            customerEmail,
            customerName,
            customerNotes,
            customerPhone,
            isNational,
            orderAbsoluteUrl,
            orderCreatedTimestamp,
            orderCreatedUserName,
            orderFulfillmentList,
            orderGrandTotal,
            orderId,
            orderStatusDescription,
          }) => {
            return {
              confirmationNumber:
                confirmationNumber || t("quoteReport.notAvailable"),
              createdBy: orderCreatedUserName,
              customerEmail,
              customerName,
              customerNotes: t(
                customerNotes ? "quoteReport.yes" : "quoteReport.notAvailable"
              ),
              customerPhone,
              dateCreated: new Date(orderCreatedTimestamp),
              deliveryStatus: orderStatusDescription[language],
              orderNumber: Number(orderId),
              orderTotal: Number(orderGrandTotal),
              poNumber:
                orderFulfillmentList
                  .map(({ poNumber }) => poNumber)
                  .filter(Boolean)
                  .join(", ") || t("quoteReport.notAvailable"),
              type: t(
                Number(isNational)
                  ? "quoteReport.isNational"
                  : "quoteReport.isDealer"
              ),
              viewOrders: removeHostFromUrl(orderAbsoluteUrl[language]),
            };
          }
        )
        .sort(sortFunc)
        .slice(0, size),
    [data, language, datePredicate, criteriaPredicate, sortFunc]
  );

  return { data: mappedData, ...rest };
};

type Payload = OrderDetails[];

export type MappedDealerOrderItem = {
  confirmationNumber: string;
  createdBy: string;
  customerEmail: string;
  customerName: string;
  customerNotes: string;
  customerPhone: string;
  dateCreated: Date;
  deliveryStatus: string;
  orderNumber: number;
  orderTotal: number;
  poNumber: string;
  type: string;
  viewOrders: string;
};
